import { useState, useMemo, useEffect } from 'react';
import { Dialog, DialogContent, DialogTitle } from './ui/dialog';
import { User, CreditCard, Sliders, Activity, Key, Calendar, Webhook, Plug, Shield, Trash2, Fingerprint, LifeBuoy, CloudSun, Code, FileSignature } from 'lucide-react';
import SettingsTabEnhanced from './SettingsTabEnhanced';
import ModalSidebarNav from './ModalSidebarNav';

const SettingsDialog = ({ open, onOpenChange }) => {
  const [activeTab, setActiveTab] = useState('settings-profile');

  // Guided tour drives the active tab via this event
  useEffect(() => {
    const setTab = (e) => { if (e.detail) setActiveTab(e.detail); };
    window.addEventListener('settings-set-tab', setTab);
    return () => window.removeEventListener('settings-set-tab', setTab);
  }, []);
  
  const isTeamMember = useMemo(() => {
    return localStorage.getItem('userType') === 'teamMember';
  }, [open]);

  // Passkeys tab — shown for BOTH admin and team members
  const passkeyTab = { id: 'settings-passkeys', label: 'Passkeys', icon: Fingerprint };

  const supportTab = { id: 'settings-support', label: 'Need help?', icon: LifeBuoy };
  const apiDocsLink = { id: 'settings-api-docs', label: 'API docs', icon: Code };

  const handleTabChange = (id) => {
    if (id === 'settings-api-docs') {
      window.open('/developers', '_blank', 'noopener');
      return;
    }
    setActiveTab(id);
  };

  const adminTabs = [
    { id: 'settings-profile', label: 'Profile', icon: User },
    { id: 'settings-billing', label: 'Billing', icon: CreditCard },
    { id: 'settings-calendar', label: 'Calendar', icon: Calendar },
    { id: 'settings-weather', label: 'Weather', icon: CloudSun },
    // Waivers tab removed — waivers live under the Customers section.
    // Integrations + Webhooks moved to the Marketing group (sidebar →
    // Integrations / Webhooks tabs) — Aug 2026 consolidation.
    { id: 'settings-2fa', label: '2FA Security', icon: Shield },
    { id: 'settings-api-keys', label: 'API Keys', icon: Key },
    { id: 'settings-activity', label: 'Log book', icon: Activity },
    apiDocsLink,
    supportTab,
    { id: 'settings-danger', label: 'Danger Zone', icon: Trash2, danger: true },
  ];

  const teamMemberTabs = [
    { id: 'settings-profile', label: 'My Profile', icon: User },
    { id: 'settings-calendar', label: 'Calendar', icon: Calendar },
    { id: 'settings-2fa', label: '2FA Security', icon: Shield },
    { id: 'settings-activity', label: 'My Activity', icon: Activity },
    supportTab,
  ];

  const tabs = isTeamMember ? teamMemberTabs : adminTabs;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="sm:max-w-5xl h-[85vh] overflow-hidden flex flex-col p-0 sm:rounded-xl"
        onInteractOutside={(e) => { if (window.__bfTourActive) e.preventDefault(); }}
        onEscapeKeyDown={(e) => { if (window.__bfTourActive) e.preventDefault(); }}
      >
        <DialogTitle className="sr-only">Settings</DialogTitle>
        {/* Mobile: Horizontal Tabs (< 768px) */}
        <div className="md:hidden bg-gray-50 overflow-x-auto flex-shrink-0">
          <div className="px-4 pt-4 pb-2">
            <h2 className="text-lg font-semibold text-gray-900 tracking-tight">Settings</h2>
          </div>
          <div className="flex gap-1.5 p-2 min-w-max">
            {tabs.map((tab) => {
              const isActive = activeTab === tab.id;
              return (
                <button
                  key={tab.id}
                  onClick={() => handleTabChange(tab.id)}
                  data-testid={`settings-tab-btn-${tab.id}`}
                  className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-medium whitespace-nowrap transition-all ${
                    isActive
                      ? tab.danger
                        ? 'bg-red-50 text-red-700'
                        : 'bg-blue-50 text-blue-700'
                      : tab.danger
                        ? 'text-red-600 bg-white'
                        : 'text-gray-600 bg-white'
                  }`}
                >
                  <tab.icon className="w-3.5 h-3.5 flex-shrink-0" />
                  <span>{tab.label}</span>
                </button>
              );
            })}
          </div>
        </div>
        
        {/* Desktop: Sidebar + Content (>= 768px) */}
        <div className="flex flex-1 overflow-hidden min-h-0">
          {/* Left Sidebar - reusable, matches the dashboard sidebar */}
          <ModalSidebarNav
            title="Settings"
            tabs={tabs}
            activeTab={activeTab}
            onTabChange={handleTabChange}
          />

          {/* Right Content Area - Full Width on Mobile */}
          <div className="flex-1 overflow-y-auto p-4 md:p-6 min-h-0 rounded-tr-2xl pt-12 md:pt-16">
            <SettingsTabEnhanced activeSubView={activeTab} />
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default SettingsDialog;